import { useEffect, useState } from "react";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { toDateInputValue, fromDateInputValue, toDateString } from "../../app/datetimeUtils";
import ModalForm from "../../components/ModalForm";
import { AddEditBroadcastAlertForm, BroadcastAlertFormData } from "./AddEditBroadcastAlertForm";
import {
	BaseAddEditBroadcastAlertFormViewModel,
	useAddAdminAlertMutation,
	useEditAdminAlertMutation,
	useGetAddAdminAlertsDataQuery,
	useGetEditAdminAlertDataQuery,
} from "./alertsApiSlice";

interface AddEditBroadcastAlertProps {
	dialogOpen: boolean;
	setDialogOpen: (value: boolean) => void;
	editId?: number;
}

const createDefaultFormData = (): BroadcastAlertFormData => ({
	message: "",
	beginDate: "",
	expireDate: toDateInputValue(new Date()),
	districtIds: [],
	schoolIds: [],
	roleValues: [],
});

function mapFormDataToViewModel(
	formData: BroadcastAlertFormData
): BaseAddEditBroadcastAlertFormViewModel {
	return {
		message: formData.message,
		beginDate: fromDateInputValue(formData.beginDate),
		expireDate: fromDateInputValue(formData.expireDate)!,
		districtIds: formData.districtIds,
		schoolIds: formData.schoolIds,
		roleValues: formData.roleValues,
	};
}

export function AddEditBroadcastAlert({
	dialogOpen,
	setDialogOpen,
	editId,
}: AddEditBroadcastAlertProps) {
	const isEditMode = editId !== undefined;

	const addQuery = useGetAddAdminAlertsDataQuery(undefined, {
		skip: isEditMode,
	});

	const editQuery = useGetEditAdminAlertDataQuery(editId!, {
		skip: !isEditMode,
	});

	const viewModel = isEditMode ? editQuery.data : addQuery.data;

	const [formData, setFormData] = useState<BroadcastAlertFormData>(createDefaultFormData);
	const [initialFormData, setInitialFormData] =
		useState<BroadcastAlertFormData>(createDefaultFormData);
	const [saveError, setSaveError] = useState("");

	const [addAlert] = useAddAdminAlertMutation();
	const [editAlert] = useEditAdminAlertMutation();

	useEffect(() => {
		if (!viewModel) {
			return;
		}

		const mappedData = mapViewModelToFormData(viewModel);
		setFormData(mappedData);

		if (isEditMode) {
			setInitialFormData(mappedData);
		}
	}, [viewModel, isEditMode]);

	function mapViewModelToFormData(
		viewModel: BaseAddEditBroadcastAlertFormViewModel
	): BroadcastAlertFormData {
		return {
			message: viewModel.message,
			beginDate: toDateString(viewModel.beginDate),
			expireDate: isEditMode ? toDateString(viewModel.expireDate)! : "",
			districtIds: viewModel.districtIds,
			schoolIds: viewModel.schoolIds,
			roleValues: viewModel.roleValues,
		};
	}

	function updateFormData<K extends keyof BroadcastAlertFormData>(
		field: K,
		value: BroadcastAlertFormData[K]
	) {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	}

	function handleSubmitAlert() {
		setSaveError("");
		const model = mapFormDataToViewModel(formData);
		const mutation = isEditMode ? editAlert({ id: editId!, model }) : addAlert(model);

		mutation
			.unwrap()
			.then(() => {
				setDialogOpen(false);
			})
			.catch((error: any) => {
				setSaveError(error?.data ?? "Failed to save alert.");
				console.error(`Failed to ${isEditMode ? "edit" : "add"} alert:`, error);
			});
	}

	// Normalize form data for comparison
	const normalizeForComparison = (data: BroadcastAlertFormData) => ({
		message: data.message,
		beginDate: data.beginDate || null,
		expireDate: data.expireDate,
		districtIds: [...data.districtIds].sort((a, b) => a - b),
		schoolIds: [...data.schoolIds].sort((a, b) => a - b),
		roleValues: [...data.roleValues].sort(),
	});

	// Check if form data has changed in edit mode
	const hasChanges = () => {
		if (!isEditMode) return true;

		return (
			JSON.stringify(normalizeForComparison(initialFormData)) !==
			JSON.stringify(normalizeForComparison(formData))
		);
	};

	const disableConfirm =
		!formData.message || !formData.expireDate || (isEditMode && !hasChanges());

	if (!viewModel) {
		return null;
	}

	return (
		<ModalForm
			open={dialogOpen}
			title={isEditMode ? "Edit Broadcast Alert" : "Add Broadcast Alert"}
			form={
				<>
					<AlertMessage
						className="my-2"
						type={AlertLevel.Error}
						message={saveError}
						showIcon
						onClear={() => setSaveError("")}
					/>
					<AddEditBroadcastAlertForm
						viewModel={viewModel}
						formData={formData}
						updateFormData={updateFormData}
					/>
				</>
			}
			confirmLabel="Save"
			disableConfirm={disableConfirm}
			onConfirm={handleSubmitAlert}
			onCancel={() => setDialogOpen(false)}
		/>
	);
}
